import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import PageNavigation from "@/components/PageNavigation";
import SEOHead from "@/components/seo/SEOHead";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Clock, User, BookOpen } from "lucide-react";
import coverConsultoria from "@/assets/cover-consultoria-de-vendas.jpg";

const ConsultoriaDeVendas = () => {
  const navigate = useNavigate();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Consultoria de Vendas: O Que É, Quanto Custa e Que Resultado Esperar",
    description:
      "O que é consultoria de vendas, como funciona um projeto etapa por etapa, quanto custa, quanto tempo leva para dar resultado e como escolher quem contratar.",
    image: "https://bianchimbusiness.com.br/cover-consultoria-de-vendas.jpg",
    datePublished: "2026-09-08",
    author: { "@type": "Person", name: "Junior Bianchim" },
    publisher: {
      "@type": "Organization",
      name: "Bianchim Business",
      logo: { "@type": "ImageObject", url: "https://bianchimbusiness.com.br/logo.png" },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": "https://bianchimbusiness.com.br/posts/consultoria-de-vendas",
    },
  };

  const etapas = [
    {
      etapa: "Diagnóstico",
      prazo: "2 semanas",
      foco: "Entender onde o dinheiro está vazando: origem de leads, funil, abordagem, números.",
    },
    {
      etapa: "Desenho do processo",
      prazo: "2 semanas",
      foco: "Etapas do funil, critério de lead qualificado, cadência, script e playbook.",
    },
    {
      etapa: "Treinamento aplicado",
      prazo: "4 semanas",
      foco: "Simulações com o produto e o cliente real da empresa, com avaliação individual.",
    },
    {
      etapa: "Acompanhamento",
      prazo: "A partir do 3º mês",
      foco: "Reunião de pipeline, forecast, correção de rota e treino contínuo.",
    },
  ];

  const investimento = [
    {
      formato: "Palestra ou workshop pontual",
      quando: "Evento de kickoff, convenção, virada de ano comercial.",
      esperar: "Energia e alinhamento de mensagem. Não muda processo sozinho.",
    },
    {
      formato: "Treinamento in company (2 a 3 encontros)",
      quando: "O processo existe, mas a execução do time está fraca.",
      esperar: "Melhora de abordagem, objeção e follow-up em poucas semanas.",
    },
    {
      formato: "Projeto de estruturação (ciclo de 3 meses)",
      quando: "A operação depende de pessoas, não de sistema.",
      esperar: "Funil, playbook, indicadores e ritual de gestão implantados.",
    },
    {
      formato: "Acompanhamento recorrente",
      quando: "A estrutura já existe e o objetivo é escalar sem perder padrão.",
      esperar: "Previsibilidade mês a mês e formação de líderes internos.",
    },
  ];

  return (
    <>
      <SEOHead
        title="Consultoria de Vendas: O Que É, Quanto Custa e Que Resultado Esperar"
        description="O que é consultoria de vendas, como funciona um projeto etapa por etapa, quanto custa, em quanto tempo dá resultado e como escolher quem contratar sem cair em promessa vazia."
        keywords="consultoria de vendas, o que é consultoria de vendas, consultoria comercial, quanto custa consultoria de vendas, consultoria comercial para empresas, estruturação comercial"
        canonicalUrl="https://bianchimbusiness.com.br/posts/consultoria-de-vendas"
        ogType="article"
        articlePublishedTime="2026-09-08"
        articleAuthor="Junior Bianchim"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <div className="min-h-screen bg-background">
        <Header />

        <main>
          <article>
            {/* Hero */}
            <section className="relative">
              <div className="w-full h-[46vh] sm:h-[58vh] relative overflow-hidden">
                <img
                  src={coverConsultoria}
                  alt="Consultor de vendas apresentando o plano de crescimento comercial para dois empresários"
                  className="w-full h-full object-cover"
                  width={1600}
                  height={912}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
              </div>
              <div className="container mx-auto px-4 relative -mt-28 sm:-mt-40 z-10">
                <div className="max-w-4xl mx-auto">
                  <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-semibold mb-4">
                    Estruturação Comercial
                  </span>
                  <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight mb-4 text-foreground">
                    Consultoria de vendas:{" "}
                    <span className="text-gradient-brand">
                      o que é, quanto custa e que resultado esperar
                    </span>
                  </h1>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mt-6">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" /> <span>Junior Bianchim</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" /> <span>11 min de leitura</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="w-4 h-4" /> <span>08 Set 2026</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Conteúdo */}
            <section className="container mx-auto px-4 py-12">
              <div className="max-w-4xl mx-auto space-y-8 text-base sm:text-lg leading-relaxed text-muted-foreground">
                <p>
                  Quase todo empresário que procura consultoria de vendas chega com a mesma frase:
                  "meu time não vende como poderia". Só que a causa raramente é preguiça. É quase
                  sempre ausência de sistema — ninguém sabe de onde vem o próximo lead, o que é uma
                  oportunidade de verdade, quantas tentativas de contato são o padrão da casa e o que
                  deve ser dito na terceira ligação.
                </p>
                <p>
                  Consultoria de vendas é o trabalho de construir esse sistema e ensinar o time a
                  rodar dentro dele. Aqui vai o que é, como funciona etapa por etapa, quanto custa,
                  quanto tempo leva para aparecer resultado e como não contratar promessa vazia.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  1. O que é consultoria de vendas
                </h2>
                <p>
                  É um projeto conduzido por um especialista externo que diagnostica a operação
                  comercial e reconstrói o que trava o resultado: geração de demanda, funil, abordagem,
                  proposta, negociação, indicadores e rotina de gestão. O consultor não vende para os
                  seus clientes — ele aumenta a capacidade da empresa de vender.
                </p>
                <p>
                  A diferença em relação a um curso é o contexto. Curso ensina o conceito; consultoria
                  aplica o conceito ao seu produto, ao seu ticket, ao seu ciclo de venda e às objeções
                  que os seus clientes realmente fazem. A diferença em relação a treinamento é o
                  escopo: treinamento desenvolve pessoas, consultoria organiza o sistema em volta
                  delas. No mundo real, os dois precisam andar juntos — é o que explico no{" "}
                  <Link to="/posts/treinamento-de-vendas" className="text-primary hover:underline">
                    guia de treinamento de vendas
                  </Link>.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  2. Como funciona um projeto, etapa por etapa
                </h2>
                <p>
                  Um projeto sério tem começo, meio e entregáveis nomeados. Este é o desenho que usamos
                  em ciclos de três meses:
                </p>

                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="w-full text-sm text-left">
                    <thead className="bg-muted/50 text-foreground">
                      <tr>
                        <th className="p-3 font-semibold">Etapa</th>
                        <th className="p-3 font-semibold">Prazo</th>
                        <th className="p-3 font-semibold">Foco</th>
                      </tr>
                    </thead>
                    <tbody>
                      {etapas.map((item, i) => (
                        <tr key={i} className="border-t border-border">
                          <td className="p-3 font-medium text-foreground">{item.etapa}</td>
                          <td className="p-3 whitespace-nowrap">{item.prazo}</td>
                          <td className="p-3">{item.foco}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <p>
                  O diagnóstico é a etapa que ninguém deveria pular. Ouvir dez ligações reais revela
                  mais sobre a operação do que qualquer relatório: dá para perceber em segundos se o
                  vendedor pergunta antes de apresentar, se ele confirma orçamento, se ele marca
                  próximo passo com data.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  3. Quanto custa uma consultoria de vendas
                </h2>
                <p>
                  Não existe tabela única, e desconfie de quem dá preço antes de entender a operação. O
                  investimento varia com o tamanho do time, a complexidade do produto, o número de
                  encontros e o tempo de acompanhamento. O que ajuda a decidir é comparar formatos:
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  {investimento.map((item, i) => (
                    <div key={i} className="p-5 rounded-xl border border-border bg-card">
                      <h3 className="font-bold text-foreground mb-2">{item.formato}</h3>
                      <p className="text-sm mb-2">
                        <strong className="text-primary">Quando faz sentido:</strong> {item.quando}
                      </p>
                      <p className="text-sm">
                        <strong className="text-primary">O que esperar:</strong> {item.esperar}
                      </p>
                    </div>
                  ))}
                </div>

                <p>
                  Uma conta simples ajuda a enxergar o retorno. Se a empresa fecha 10 negócios por mês
                  com ticket médio de R$ 5.000, e o projeto eleva a conversão de 10% para 13%, são três
                  negócios a mais por mês — R$ 15.000 de receita adicional recorrente. É esse número,
                  e não o preço da hora do consultor, que deve guiar a decisão.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  4. Que resultado esperar (e em quanto tempo)
                </h2>
                <p>
                  Nas primeiras semanas aparecem os ganhos de execução: mais reuniões marcadas porque a
                  abordagem melhorou, menos oportunidade perdida por falta de follow-up, propostas mais
                  claras. É o efeito de arrumar o básico.
                </p>
                <p>
                  Entre o segundo e o terceiro mês vem o ganho estrutural: o pipeline passa a ser
                  confiável, o gestor consegue prever o fechamento com margem pequena de erro, e o
                  vendedor novo entra num processo pronto em vez de aprender por osmose. A partir daí o
                  crescimento vira consequência de rotina, não de esforço heroico.
                </p>
                <p>
                  O que consultoria nenhuma entrega: resultado sem envolvimento do gestor. Se a
                  liderança não participa das reuniões de pipeline e não cobra o processo, o time volta
                  ao antigo em quatro semanas. Esse é, de longe, o principal motivo de projeto que não
                  pega.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  5. Como escolher quem contratar
                </h2>
                <p>
                  Peça exemplos de operações parecidas com a sua em tamanho e modelo de venda. Peça o
                  formato do diagnóstico e o que fica documentado. Pergunte quais indicadores serão
                  acompanhados e qual a meta de movimento em cada um. E pergunte o que acontece depois
                  do último encontro: quem sustenta o treino, com que frequência, com qual ferramenta.
                </p>
                <p>
                  Essa última pergunta é a que separa projeto que dura de projeto que virou pasta no
                  Drive. Treino comercial precisa de repetição semanal, e ninguém sustenta isso na base
                  da boa vontade — por isso usamos a plataforma com IA para o time continuar treinando
                  objeção e fechamento entre os encontros. Vale ver também nosso material sobre{" "}
                  <Link to="/posts/playbook-de-vendas" className="text-primary hover:underline">
                    playbook de vendas
                  </Link>{" "}
                  e{" "}
                  <Link to="/posts/metricas-e-kpis-de-vendas" className="text-primary hover:underline">
                    métricas e KPIs de vendas
                  </Link>.
                </p>

                <h2 className="text-2xl sm:text-3xl font-bold text-foreground pt-4">
                  Conclusão: consultoria é investimento em previsibilidade
                </h2>
                <p>
                  Empresa que contrata consultoria de vendas pelo motivo certo não está comprando
                  motivação: está comprando previsibilidade. Saber de onde vem o próximo negócio, com
                  qual conversa ele é conquistado e quanto o time consegue entregar no mês.
                </p>
                <p>
                  Se quiser ver como isso funcionaria na sua operação, conheça nossa{" "}
                  <Link to="/consultoria" className="text-primary hover:underline">
                    consultoria comercial e estruturação de time de vendas
                  </Link>{" "}
                  — com diagnóstico, prazos e entregáveis definidos desde o primeiro encontro.
                </p>

                <div className="flex flex-wrap gap-3 pt-4">
                  <Button
                    onClick={() => navigate("/consultoria")}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    Falar sobre um diagnóstico comercial
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                  <Button variant="outline" onClick={() => navigate("/auth?mode=signup")}>
                    Criar conta grátis e treinar o time
                  </Button>
                </div>
              </div>
            </section>

            <PageNavigation />
          </article>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default ConsultoriaDeVendas;
